import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging and auth token
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add auth token if available
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    
    // Handle different error types
    if (error.response?.status === 401) {
      throw new Error('Not authenticated. Please log in.');
    } else if (error.response?.status === 404) {
      throw new Error('Resource not found');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data.detail || 'Invalid request');
    } else if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout. Please check your connection.');
    } else {
      throw new Error(error.response?.data?.detail || error.message || 'An error occurred');
    }
  }
);

// Vocabulary API
export const vocabularyAPI = {
  // Get all vocabulary with optional filters
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.search) params.append('search', filters.search);
    if (filters.difficulty) params.append('difficulty', filters.difficulty);
    
    const response = await apiClient.get(`/vocabulary?${params}`);
    return response.data;
  },

  // Create new vocabulary word
  create: async (wordData) => {
    const response = await apiClient.post('/vocabulary', wordData);
    return response.data;
  },

  // Update vocabulary word
  update: async (wordId, updateData) => {
    const response = await apiClient.put(`/vocabulary/${wordId}`, updateData);
    return response.data;
  },

  // Delete vocabulary word
  delete: async (wordId) => {
    const response = await apiClient.delete(`/vocabulary/${wordId}`);
    return response.data;
  }
};

// Categories API
export const categoriesAPI = {
  // Get all categories
  getAll: async () => {
    const response = await apiClient.get('/categories');
    return response.data;
  },
  
  // Create new category (admin only)
  create: async (categoryData) => {
    const response = await apiClient.post('/categories', categoryData);
    return response.data;
  }
};

// Progress API
export const progressAPI = {
  // Get user progress
  get: async () => {
    const response = await apiClient.get('/progress');
    return response.data;
  },

  // Update progress after game
  update: async (progressData) => {
    const response = await apiClient.post('/progress', progressData);
    return response.data;
  }
};

// Games API
export const gamesAPI = {
  // Get questions for specific game type
  getQuestions: async (gameType, options = {}) => {
    const params = new URLSearchParams();
    if (options.count) params.append('count', options.count);
    if (options.category) params.append('category', options.category);
    if (options.difficulty) params.append('difficulty', options.difficulty);
    
    const response = await apiClient.get(`/games/questions/${gameType}?${params}`);
    return response.data;
  },

  // Save game session
  saveSession: async (sessionData) => {
    const response = await apiClient.post('/games/session', sessionData);
    return response.data;
  }
};

// Health check API
export const healthAPI = {
  check: async () => {
    const response = await apiClient.get('/health');
    return response.data;
  }
};

// Helper function to handle API errors gracefully
export const handleApiError = (error, defaultMessage = 'An error occurred') => {
  console.error('API Error:', error);
  return {
    success: false,
    message: error.message || defaultMessage
  };
};

// Helper function for successful API responses
export const handleApiSuccess = (data, message = 'Operation successful') => {
  return {
    success: true,
    data,
    message
  };
};

export default apiClient;